import Controller from '@ember/controller';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { showModal } from 'sh-www/utils/modal-controls';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { dropTask } from 'ember-concurrency';
import ENV from 'sh-www/config/environment';

class SettingsPayoutController extends Controller {
  @service('state-managers/application') applicationStateManager;
  @service('error-tracker') errorTracker;
  @service router;
  @service('commands/payout-account-commands') payoutAccountCommandService;
  @service session;
  @service store;

  get queryParams() {
    return ['payoneerRedirect'];
  }

  /** @type {Boolean} */
  refreshFailed = false;

  @dropTask
  *loadUser(refresh = false) {
    const { currentUser } = this.session;

    try {
      if (refresh) {
        yield this.payoutAccountCommandService.syncPayoutAccounts(currentUser);
      }

      const user = yield this.store.findRecord('user', currentUser.id, {
        include: 'payout-accounts',
        reload: true,
      });

      this.set('model.user', user);

      if (refresh) {
        this.applicationStateManager.addToast('Payout method updated');
      }
    } catch (error) {
      handleErrorResponseWithModal(this, error);
      this.errorTracker.error(error, this);
    }
  }

  @dropTask
  *removePayoutAccount(payoutAccount) {
    try {
      yield this.payoutAccountCommandService.setLinkedStatus(payoutAccount, false);

      const user = yield this.store.findRecord('user', this.session.currentUser.id, {
        include: 'payout-accounts',
        reload: true,
      });

      this.set('model.user', user);

      this.applicationStateManager.addToast('Payout method removed');
    } catch (error) {
      handleErrorResponseWithModal(this, error);
      this.errorTracker.error(error, this);
    }
  }

  @dropTask
  *selectPayoutAccount(payoutAccount) {
    try {
      yield this.payoutAccountCommandService.setPayoutAccountPreferred(payoutAccount);

      const user = yield this.store.findRecord('user', this.session.currentUser.id, {
        include: 'payout-accounts',
        reload: true,
      });

      this.set('model.user', user);

      this.applicationStateManager.addToast('Payout method selected');
    } catch (error) {
      handleErrorResponseWithModal(this, error);
      this.errorTracker.error(error, this);
    }
  }

  @dropTask
  *refreshPayoutAccounts() {
    const { currentUser } = this.session;

    try {
      yield this.payoutAccountCommandService.syncPayoutAccounts(currentUser);

      const user = yield this.store.findRecord('user', currentUser.id, {
        include: 'payout-accounts',
        reload: true,
      });

      this.set('model.user', user);

      this.applicationStateManager.addToast('Payout methods refreshed');
      this.set('refreshFailed', false);
    } catch (error) {
      this.errorTracker.error(error, this);
      this.set('refreshFailed', true);
    }
  }

  @action
  handleCreate() {
    this.router.transitionTo('settings.payout.add-method');
  }

  @action
  handleRemove(payoutAccount) {
    showModal(this, 'confirmation', {
      titleText: 'Remove this payout method?',
      bodyText: 'You will no longer be able to receive payouts via this method.',
      actionButtonText: 'Remove',
      isShowCancelButton: true,
      confirmAction: () => {
        this.removePayoutAccount.perform(payoutAccount);
      },
    });
  }

  @action
  handleSelect(payoutAccount) {
    showModal(this, 'confirmation', {
      titleText: 'Select this payout method?',
      bodyText: 'If selected, payouts will be sent via this payout method.<br><br>Your previously selected payout method will still be available. We suggest you do not remove it until you have successfully received a payout via this new method.',
      actionButtonText: 'Select',
      isShowCancelButton: true,
      confirmAction: () => {
        this.selectPayoutAccount.perform(payoutAccount);
      },
    });
  }

  @action
  handleRefresh() {
    showModal(this, 'confirmation', {
      titleText: 'Refresh status',
      bodyText: `If you believe your setup is showing an inaccurate status, we can refresh your information.<br><br>If you continue to run into issues, <a href="${ENV.supportEmail}" class="black-underline lsg-black">contact our support team</a>.`,
      actionButtonText: 'Refresh',
      isShowCancelButton: true,
      confirmAction: () => {
        this.refreshPayoutAccounts.perform();
      },
    });
  }

  @action
  handleVerify() {
    this.router.transitionTo('settings.payout.payee-verification');
  }
}

export default SettingsPayoutController;
